import React, { useCallback, useState } from 'react';

import { formBridge } from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Button, Typography } from 'antd';
import { AutoForm, AutoFields, NumField, AutoField } from 'uniforms-antd';

import { ReturnDisclaimer } from './ReturnDisclaimer';
import { t } from '../../config/translations';
import { useProduct } from '../../products/hooks';
import { returnERMreasons, returnECSreasons } from '../../utils/communSchemas';
import { ArticleType, searchArticleType } from '../utils/returnDisclaimerContent';

// Register this component for debug tracking
debug.registerComponent('AddReturnForm');

export type ReturnFormData = {
  qret?: string;
  cact?: 'ERM' | 'ECS' | 'REM';
  clit?: string;
  size?: string;
  new_nart?: string;
  nart?: string;
  desi?: string;
};

export type ReturnSizes = {
  size: string;
  nart: string;
}[];

export type ReturnFormProps = {
  maxQuantity: number;
  onSubmit: (formData: ReturnFormData) => void;
  data?: ReturnFormData;
};

const returnReason = (returnType: ReturnFormData['cact']) => {
  if (returnType === 'ERM' || returnType === 'REM') {
    return returnERMreasons;
  }

  return returnECSreasons;
};

const returnSchema = (returnType: ReturnFormData['cact'], nart?: string) => ({
  title: 'Return',
  type: 'object',
  errorMessage: {
    required: {
      get qret() {
        return t('error.quantityRequired');
      },
      get cact() {
        return t('error.returnTypeRequired');
      },
      get clit() {
        return t('error.returnReasonRequired');
      },
    },
    properties: {
      get qret() {
        return t('error.quantityMin');
      },
    },
  },
  properties: {
    qret: {
      type: 'integer',
      get title() {
        return t('form.quantityReturned');
      },
      uniforms: {
        component: NumField,
      },
    },
    cact: {
      type: 'string',
      get title() {
        return t('form.returnType');
      },
      uniforms: {
        checkboxes: true,
        get options() {
          return [
            { label: t('form.creditLabel'), value: 'ERM' },
            {
              label: t('form.exchangeLabel'),
              value: 'ECS',
              disabled: nart?.endsWith('000'),
            },
            // { label: t('form.refund'), value: 'REM' },
          ];
        },
      },
    },
    clit: {
      type: 'string',
      get title() {
        return t('form.returnReason');
      },
      uniforms: {
        options: returnReason(returnType),
      },
    },
  },
  required: ['qret', 'cact', 'clit'],
});

const sizeSchema = (options?: ReturnSizes) => ({
  title: 'Return',
  type: 'object',
  errorMessage: {
    required: {
      get size() {
        return t('error.sizeRequired');
      },
    },
  },
  properties: {
    size: {
      type: 'string',
      get title() {
        return t('form.newSizeRequested');
      },
      uniforms: {
        options: options
          ?.map((option) => ({
            label: option.size,
            value: `${option?.size}/${option?.nart}`,
          }))
          .sort((a, b) => (a.label > b.label ? 1 : -1)),
      },
    },
    new_nart: {
      type: 'string',
      uniforms: { component: () => null },
    },
  },
  required: ['size'],
});

const AddReturnForm = (props: ReturnFormProps) => {
  debug.trackRender('AddReturnForm');
  const { onSubmit, maxQuantity, data } = props;
  debug.log(
    'AddReturnForm',
    `Rendering with maxQuantity: ${maxQuantity}, nart: ${data?.nart}`,
  );

  const { data: productData, isLoading } = useProduct({ productId: data?.nart });

  const articleType: ArticleType | undefined = productData?.productName
    ? searchArticleType(productData?.productName!)
    : undefined;

  const [formData, setFormData] = useState<ReturnFormData>(
    data
      ? {
          qret: data.qret,
          cact: data.cact,
          clit: data.clit,
          size: data.size,
          new_nart: data.new_nart,
          nart: data.nart,
          desi: data.desi,
        }
      : {},
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const schema = formBridge(returnSchema(formData?.cact || data?.cact, data?.nart));
  const schemaSize = formBridge(sizeSchema(productData?.sizes));

  let formRef: any = {};
  let formSizeRef: any = {};

  const hasSize = formData?.cact === 'ECS';

  const handleSubmit = useCallback(async () => {
    debug.log('AddReturnForm', `Submitting return form`);
    if (formRef?.submit) {
      await formRef.submit();
    }
    if (formSizeRef?.submit) {
      await formSizeRef.submit();
    }
    onSubmit(formData);
  }, [formRef, formSizeRef]);

  const handleFormDataChange = useCallback(
    (key: string, value: string) => {
      setFormData({ ...formData, [key]: value });
    },
    [formData],
  );

  const handleFormDataSizeChange = useCallback(
    (key: string, value: string) => {
      setFormData({
        ...formData,
        [key]: value.split('/')?.[0],
        new_nart: value.split('/')?.[1],
      });
    },
    [formData],
  );

  const handleOpenModal = () => {
    const validations = [];

    if (formRef?.validate) {
      validations.push(formRef.validate());
    }
    if (hasSize && formSizeRef?.validate) {
      validations.push(formSizeRef.validate());
    }
    Promise.all(validations).then((results) => {
      if (results?.every((result) => result === null)) {
        if ((formData.clit === '009' || formData.clit === '010') && articleType) {
          setIsModalOpen(true);
        } else {
          setIsModalOpen(false);
          handleSubmit();
        }
      }
    });
  };

  const handleOk = () => {
    handleSubmit();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Typography.Title level={5} style={{ marginTop: '12px' }}>
        {t('form.chooseQuantity')}
      </Typography.Title>
      <AutoForm
        model={{ ...data, qret: Number(data?.qret) }}
        schema={schema}
        showInlineError
        errorsField={() => null}
        onChange={handleFormDataChange}
        ref={(ref) => {
          formRef = ref;
        }}
      >
        <NumField name="qret" min={1} max={maxQuantity} />
        <AutoField name="cact" />
        {data?.nart?.endsWith('000') && (
          <Typography.Text type="warning">
            {t('form.exchangeUnavailable')}
          </Typography.Text>
        )}
        <AutoField name="clit" />
      </AutoForm>

      {formData?.cact === 'REM' && (
        <Typography.Text style={{ fontWeight: 'bold' }} type="danger">
          {t('form.ribWarning')}
        </Typography.Text>
      )}

      {hasSize && !isLoading && (
        <AutoForm
          model={{ size: data?.size }}
          schema={schemaSize}
          onSubmit={() => {}}
          showInlineError
          errorsField={() => null}
          onChange={handleFormDataSizeChange}
          ref={(ref) => {
            formSizeRef = ref;
          }}
        >
          <AutoFields />
        </AutoForm>
      )}

      <div className="full-right">
        <Button type="primary" onClick={handleOpenModal}>
          {t('button.validateReturn')}
        </Button>
      </div>

      {articleType && (formData.clit === '009' || formData.clit === '010') && (
        <ReturnDisclaimer
          articleType={articleType}
          returnReason={formData.clit === '009' ? 'GRAND' : 'PETIT'}
          isModalOpen={isModalOpen}
          handleOk={handleOk}
          handleCancel={handleCancel}
        />
      )}
    </>
  );
};

export default AddReturnForm;

// Mark this component as initialized
debug.markInitialized('AddReturnForm');
